import React, { Dispatch, useEffect, useState } from 'react';
import { Content, Header, MainSubtitle, MainTitle, Page } from './styled';
import LogoIcon from 'assets/svg/il-logo.svg';
import { useLocale } from 'locale';
import { IInvestorPreferences, LoadType, MutationType } from 'store/sagas/apiService/types';
import { connect } from 'react-redux';
import { RootAction, State } from 'store/state';
import { inlineMutate } from 'store/state/mutationsResponse/actions';
import { SpacingBlock } from 'components/spacing-block';
import { navigateTo as navigateToFn } from 'store/state/router/actions';
import { DecodedRouteParams, NavigateToFn, Route } from 'config/routes';
import { investorCalculatedBudgetDataSelector, investorPreferencesDataSelector } from 'store/state/domainData/selectors/investors';
import Spinner from 'components/widget/prices/prices-table/tables/history/affiliation/Spinner';
import { investorPreferencesSelector } from 'store/state/domainData/selectors/common';
import { Domain } from 'store/state/domainData';
import { NetworkStatus } from 'apollo-client';
import { WizardSummary } from './steps/Summary';
import { StepsContent } from './StepsContent';
import { isNil } from 'lodash';


export enum Area {
  Investment,
  Important,
  Budget,
  AnotherApartment,
}

const WizardPageBase: React.FC<{
  savedPreferences?: IInvestorPreferences;
  investorPreferences?: Domain<LoadType.InvestorPreferences>;
  onUpdatePreferences: (data: any) => void;
  navigateTo: NavigateToFn;
  calculatedBudget?: {
    loanAmount: number;
    monthlyPayment: number;
    totalBudget: number;
  };
}> = ({ savedPreferences, onUpdatePreferences, navigateTo, investorPreferences, calculatedBudget }) => {
  const { t } = useLocale();
  const [ activeArea, setActiveArea ] = useState<Area>(Area.Investment);
  const [ savedAreas, setSavedAreas ] = useState<Set<Area>>(new Set([]));
  const [ isLoading, setIsLoading ] = useState(true);
  const [ showSummary, setShowSummary ] = useState(false);

  const title = t('investors.wizard.title');
  const subtitle = t('investors.wizard.subtitle');

  useEffect(() => {
    if (!isNil(savedPreferences) && !isNil(savedPreferences.budget) && !isNil(savedPreferences.goal) && !isNil(savedPreferences.hasProperty) && !isNil(savedPreferences.places)) {
      setSavedAreas(new Set([ Area.AnotherApartment, Area.Budget, Area.Important, Area.Investment ]));
    }
  }, [ savedPreferences, setSavedAreas ]);

  useEffect(() => {
    if (investorPreferences.networkStatus === NetworkStatus.ready) {
      setIsLoading(false);
    }
  }, [ setIsLoading, investorPreferences ]);

  const submitWizard = () => {
    setShowSummary(true);
  };

  const content = showSummary ? (
    <WizardSummary calculatedBudget={calculatedBudget} navigateTo={navigateTo} />
  ) : (
    <StepsContent
      activeArea={activeArea}
      savedAreas={savedAreas}
      savedPreferences={savedPreferences}
      submitWizard={submitWizard}
      setActiveArea={setActiveArea}
      setSavedAreas={setSavedAreas}
      onUpdatePreferences={onUpdatePreferences}
    />
  );

  return (
    <Page>
      <Header>
        <LogoIcon width={118} height={22} />
      </Header>
      <Content>
        {showSummary ? null : <MainTitle>{title}</MainTitle>}
        {showSummary ? null : <MainSubtitle>{subtitle}</MainSubtitle>}
        {isLoading || (showSummary && !calculatedBudget) ? (
          <>
            <SpacingBlock mTop={5} />
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Spinner />
            </div>
            <SpacingBlock mTop={5} />
          </>
        ) : content}
      </Content>
    </Page>
  );
};

const mapStateToProps = (state: State) => ({
  savedPreferences: investorPreferencesDataSelector(state),
  investorPreferences: investorPreferencesSelector(state),
  calculatedBudget: investorCalculatedBudgetDataSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<RootAction>) => ({
  onUpdatePreferences: (data: any) => dispatch(inlineMutate(MutationType.UpdateInvestorPreferences, { input: data })),
  navigateTo: (route: Route, params: DecodedRouteParams) => dispatch(navigateToFn(route, params)),
});

export const WizardPage = connect(mapStateToProps, mapDispatchToProps)(WizardPageBase);
